import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function RutlandCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Rutland Pothole Claims | 1 in 5 Village Roads in RED | Fixtyer"
        description="Rutland maintains just 520km of road yet one in five unclassified routes has been in RED condition for five straight years. All four DfT 2025/26 scorecards are AMBER. See what this means for your pothole claim."
        canonical="/councils/rutland"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Rutland', url: '/councils/rutland' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Rutland has AMBER on every DfT scorecard — can I still claim?',
            answer: 'Yes. An AMBER overall rating does not block claims. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired in time — not on aggregate scorecard colours. Rutland\'s own data shows 19–20% of unclassified roads in RED condition for five consecutive years, pothole fills rising 64% since 2020/21, and reactive maintenance climbing to 27% of spend in 2024/25. Those are factual starting points for a claim on the defect that hurt you.',
          },
          {
            question: 'What if my pothole was on a village or residential road?',
            answer: 'Unclassified roads make up 222km — 43% of Rutland\'s 520km adopted network. The council\'s annual GAIST video survey has placed 19% of U-roads in RED every year from 2020 to 2023, rising to 20% in 2024. That is roughly 44km of village and estate routes where maintenance should be considered. Rutland surveys U-roads every year, but the council notes video-based results are refactored to fit SCANNER categories and are not directly comparable — a nuance worth raising if the council cites network-wide condition data in a refusal.',
          },
          {
            question: 'Does the missing 2022 B/C road survey weaken my claim?',
            answer: 'It can help if your incident was on a B or C road in 2022/23. Rutland states there is no B/C SCANNER data for 2022 "due to an instrument malfunction during the survey." For that financial year, the council had no published network-level condition record for 221km of classified local roads — 42% of its network. If your pothole was on a B or C route during that gap, ask what inspection evidence the council relied on when deciding it had a reasonable maintenance system.',
          },
          {
            question: 'Rutland filled 1,737 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Pothole fills have risen every year for five years: 1,057 in 2020/21 to 1,737 in 2024/25 — a total of 7,046 fills. The council states potholes are "the most common maintenance issue reported to us" and that 1,737 repairs cost slightly under £134,000, almost 13% of the reactive maintenance budget that year. A network still producing defects at that rate is one where individual potholes can form between inspections — exactly where prior reports and photographic evidence matter.',
          },
          {
            question: 'Rutland spends above its DfT allocation — why is Spend still AMBER?',
            answer: 'In 2024/25 Rutland spent £2.857m capital against a £2.381m DfT allocation, and projects matching its full £3.770m allocation in 2025/26. Yet the DfT Spend scorecard is AMBER — the rating reflects national benchmarking, not just whether spend exceeds allocation. For your claim, the more relevant figure is that reactive maintenance rose from 15% of spend in 2020/21 to 27% in 2024/25, meaning over a quarter of highway investment that year went to fixing defects after they appeared rather than preventing them.',
          },
          {
            question: 'Who maintains the A1 through Rutland?',
            answer: 'Rutland County Council is not responsible for the A1 and its slip roads. Although maintained at public expense, those routes are part of the strategic road network managed by National Highways. Rutland maintains 77km of other A-roads, 221km of B/C roads and 222km of unclassified roads. Check the National Street Gazetteer: if a road shows "Maintainable at public expense, Rutland" under responsibility, it is the council\'s — otherwise your claim may need to go elsewhere.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Rutland County Council"
        councilSlug="rutland"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/rutland"
        headline="Rutland Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Rutland</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Rutland: one in five village roads in RED — five years running
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            England&apos;s smallest historic county maintains just
            <strong className="text-white"> 520 kilometres of road</strong> — yet Rutland&apos;s own surveys
            place <strong className="text-rose-400">19–20% of unclassified routes in RED condition</strong> every
            year since 2020. All four DfT 2025/26 scorecards are
            <strong className="text-amber-400"> AMBER</strong>, pothole fills are up
            <strong className="text-rose-400"> 64%</strong> in five years, and reactive maintenance swallowed
            <strong className="text-amber-400"> 27% of highway spend</strong> in 2024/25.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">19% → 20%</div>
            <div className="text-white font-medium mt-2">U-road RED share — flat for five years</div>
            <div className="text-brand-300 text-sm mt-1">
              On 222km of village and estate roads — roughly 44km where maintenance should be considered —
              the proportion in RED barely moved while pothole repairs climbed from 1,057 to 1,737 a year.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The network context */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A small network with a large replacement bill
          </h2>
          <p className="text-surface-600 mb-8">
            Rutland&apos;s own transparency report — network scale, asset value and what the council is responsible for
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">520km</div>
              <div className="text-sm text-surface-600 mt-1">Total adopted roads</div>
              <div className="text-xs text-surface-500 mt-1">Plus 193km footways and 320km public rights of way</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">£680m</div>
              <div className="text-sm text-surface-600 mt-1">Gross replacement cost</div>
              <div className="text-xs text-surface-500 mt-1">Council&apos;s largest physical asset</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">0.64%</div>
              <div className="text-sm text-surface-600 mt-1">Of asset value spent on maintenance</div>
              <div className="text-xs text-surface-500 mt-1">2024/25 — £3.83m against £601.7m GRC</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Survey method</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">77km</td>
                  <td className="px-6 py-4 text-center">14.8%</td>
                  <td className="px-6 py-4 text-center">SCANNER (annual)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">221km</td>
                  <td className="px-6 py-4 text-center">42.5%</td>
                  <td className="px-6 py-4 text-center">SCANNER (annual)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">U roads</td>
                  <td className="px-6 py-4 text-center">222km</td>
                  <td className="px-6 py-4 text-center">42.7%</td>
                  <td className="px-6 py-4 text-center">GAIST video (annual)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total</td>
                  <td className="px-6 py-4 text-center font-bold">520km</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">A1 managed by National Highways</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="It would cost the council well over half a billion pounds (£680million) to replace every paved road and footpath in Rutland."
            source="Rutland County Council — Highways Maintenance Transparency Report"
          />
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and GAIST survey data from Rutland&apos;s own transparency report
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6 bg-white">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (77km): amber share peaked in 2023</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">82%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">82%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">27%</td>
                    <td className="px-4 py-2 text-center font-bold">70%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road amber condition jumped from 17% to 27% in 2023 before easing to 21% in 2024. The council&apos;s
              2024/25 KPI target was 74% of A-roads in good condition; the result was 76.8% — above target, but
              the 2023 spike shows principal routes are not immune to deterioration.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (221km): a blind year in 2022</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">79%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data — instrument malfunction</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads hit 4% in 2023 — the highest in five years. Combined amber and red, roughly a quarter
              of classified local roads needed or would soon need maintenance at the last two comparable surveys.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (222km): stuck at one in five RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-rose-800 text-sm mb-3">
                  Rutland surveys all unclassified roads annually with a vehicle-mounted 360° camera (GAIST). Images
                  are graded 1–5, then refactored into RED, amber and green to align with SCANNER categories.
                </p>
                <p className="text-rose-800 text-sm">
                  The council states results from the two survey types are
                  <strong> not directly comparable</strong>, and that the video survey
                  &ldquo;will generally identify more defects than SCANNER&rdquo; — yet RED U-roads have barely
                  shifted in five years.
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-rose-200">
                <h4 className="font-semibold text-rose-900 mb-2 text-sm">What 20% RED means on the ground</h4>
                <p className="text-rose-800 text-sm">
                  20% of 222km is roughly <strong>44km of village, estate and rural lanes</strong> where the
                  council&apos;s own survey says maintenance should be considered — a persistent figure, not a
                  one-off blip.
                </p>
              </div>
            </div>
          </div>

          <QuoteCard
            quote="Subject to funding, we're aiming to maintain the condition of the network of carriageways at current levels."
            source="Rutland County Council — Condition of Local Roads"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            7,046 pothole fills in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Rising reactive workload on England&apos;s smallest county highway network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change on prior year</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,057</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,160</td>
                  <td className="px-6 py-4 text-center text-amber-600">+10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,454</td>
                  <td className="px-6 py-4 text-center text-amber-600">+25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,638</td>
                  <td className="px-6 py-4 text-center text-amber-600">+13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,737</td>
                  <td className="px-6 py-4 text-center text-rose-600">+6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,046</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+64% since 2020/21</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">£134,000 on potholes alone</h3>
              <p className="text-surface-600 text-sm">
                In 2024/25 Rutland fixed 1,737 potholes at a combined cost of slightly under £134,000 — almost
                13% of the reactive maintenance budget that year. The council describes potholes as
                &ldquo;the most common maintenance issue reported to us.&rdquo;
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Nearly five fills a day</h3>
              <p className="text-amber-800 text-sm">
                Averaged over 2024/25, Rutland filled roughly 4.8 potholes every day on a 520km network. On a
                per-kilometre basis, that is a higher defect rate than many larger counties — small network, steady
                reactive pressure.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Potholes are the most common maintenance issue reported to us. In 2024 to 2025 we fixed 1737 potholes at a combined cost of slightly under £134,000, which was almost 13% of our budget for reactive maintenance that year."
            source="Rutland County Council — Highways Maintenance Spending Figures"
          />
        </div>
      </section>

      {/* Spend picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spend: above allocation, yet all AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Capital, revenue and the shift from prevention to reaction — from Rutland&apos;s spending tables
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£3,150</td>
                  <td className="px-4 py-3 text-center">£3,694</td>
                  <td className="px-4 py-3 text-center">£698</td>
                  <td className="px-4 py-3 text-center text-emerald-600">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2,381</td>
                  <td className="px-4 py-3 text-center">£2,727</td>
                  <td className="px-4 py-3 text-center">£793</td>
                  <td className="px-4 py-3 text-center text-emerald-600">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£2,381</td>
                  <td className="px-4 py-3 text-center">£2,940</td>
                  <td className="px-4 py-3 text-center">£875</td>
                  <td className="px-4 py-3 text-center text-emerald-600">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£2,804</td>
                  <td className="px-4 py-3 text-center">£3,322</td>
                  <td className="px-4 py-3 text-center">£821</td>
                  <td className="px-4 py-3 text-center text-emerald-600">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center">£2,381</td>
                  <td className="px-4 py-3 text-center font-bold">£2,857</td>
                  <td className="px-4 py-3 text-center font-bold">£1,044</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">27%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£3,770</td>
                  <td className="px-4 py-3 text-center">£3,770</td>
                  <td className="px-4 py-3 text-center">£732</td>
                  <td className="px-4 py-3 text-center text-emerald-600">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The reactive share nearly doubled</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">27%</div>
                <div className="text-brand-300 text-sm">Reactive share in 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">15%</div>
                <div className="text-brand-300 text-sm">Reactive share in 2020/21</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£3.83m</div>
                <div className="text-brand-300 text-sm">Total maintenance spend 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Rutland routinely spends <span className="text-emerald-400">more capital than the DfT allocates</span> —
              yet every 2025/26 scorecard is AMBER. For claim purposes, the telling trend is reactive maintenance
              climbing to over a quarter of total spend while U-road RED condition holds at one in five.
            </p>
          </div>
        </div>
      </section>

      {/* Best practice & satisfaction */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Best practice AMBER — what the council publishes
          </h2>
          <p className="text-surface-600 mb-8">
            Asset management, benchmarking and the gap between targets and public satisfaction
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What Rutland does well</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Highways Infrastructure Asset Management Plan, Resilient Network Plan and Winter Service Policy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on classified roads; annual GAIST survey on all U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT customer survey — top quartile nationally in fourth year of participation (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ALARM survey and University of Leeds NHT CQC benchmarking
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B-road good-condition KPI at 83.1% — above the 72% target in 2024/25
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Where the data shows strain</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway satisfaction 31.0% — below the 32.0% KPI target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Drainage satisfaction 35.0% — below the 37.0% target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road SCRIM red band 3.3% — above the 2.0% KPI target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Category 2 jobs on-time 97.2% — fractionally below the 97.5% target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Maintenance spend just 0.59–0.66% of gross replacement cost annually
                </li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="In our fourth year participating in the survey (2024) we're still placed in the top quartile nationally."
            source="Rutland County Council — Annex B: Incentive Element Questions"
          />
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an all-AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Rutland runs a documented system — here is how that shapes your claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management plan and annual condition surveys on all road classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual U-road GAIST survey — no alternate-year blind spot
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend exceeds DfT allocation in most recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Top-quartile NHT satisfaction nationally — structured benchmarking culture
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Small network means fewer roads to monitor — harder to argue systemic neglect at scale
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence. Generic template letters will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 19–20% of U-roads in RED for five consecutive years — roughly 44km of known-poor village roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,046 pothole fills in five years — defects forming faster than prevention absorbs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive spend at 27% in 2024/25 — rising repair burden
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No B/C SCANNER data in 2022 — inspection gap on 221km of classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway satisfaction below KPI target — public experience diverges from network KPIs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road and SCANNER survey results are not directly comparable
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with published asset management and annual surveys, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, cite the five-year 19–20% RED record for that network segment</li>
              <li>• Timing — if your incident fell in 2022/23 on a B or C road, raise the SCANNER instrument malfunction gap</li>
              <li>• Drainage context — satisfaction with drainage missed target at 35% vs 37%</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Rutland&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Rutland first
          </h2>
          <p className="text-surface-600 mb-6">
            Rutland&apos;s highway maintenance guidance classifies immediate dangers as emergencies. For
            non-emergency defects, reporting through FixMyStreet or MyAccount creates a dated record — useful
            evidence if the pothole was reported before your incident, or if the council failed to repair it
            within a reasonable time.
          </p>
          <a
            href="https://www.rutland.gov.uk/roads-transport-parking/highways/report-highway-issue"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Rutland County Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use FixMyStreet or MyAccount via the council&apos;s reporting page. For emergencies during office hours
            call 01572 722 577; out of hours call Harborough Lifeline on 01858 464 499. Keep your reference number.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Rutland?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A documented council demands a documented claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No five-year U-road RED record cited</li>
                <li>• No 2022 B/C survey gap argued</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 19–20% U-road RED over five years documented</li>
                <li>✅ 7,046 pothole fills and 27% reactive spend cited</li>
                <li>✅ 2022 B/C SCANNER gap argued where relevant</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Rutland</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=rutland"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Rutland Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Rutland has AMBER on every DfT scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. An AMBER overall rating does not block claims. Section 58 turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired in time — not on aggregate scorecard
                colours. Rutland&apos;s own data shows 19–20% of unclassified roads in RED condition for five consecutive
                years, pothole fills rising 64% since 2020/21, and reactive maintenance climbing to 27% of spend in
                2024/25.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a village or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 222km — 43% of Rutland&apos;s 520km adopted network. The council&apos;s
                annual GAIST video survey has placed 19% of U-roads in RED every year from 2020 to 2023, rising to
                20% in 2024. That is roughly 44km of village and estate routes where maintenance should be considered.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the missing 2022 B/C road survey weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can help if your incident was on a B or C road in 2022/23. Rutland states there is no B/C SCANNER
                data for 2022 due to an instrument malfunction during the survey. For that financial year, the council
                had no published network-level condition record for 221km of classified local roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Rutland filled 1,737 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Pothole fills have risen every year for five years to 1,737 in 2024/25 — a total of 7,046 fills.
                The council states potholes are the most common maintenance issue reported, and those 1,737 repairs
                cost slightly under £134,000 — almost 13% of the reactive maintenance budget that year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Rutland spends above its DfT allocation — why is Spend still AMBER?
              </h3>
              <p className="text-surface-600 text-sm">
                In 2024/25 Rutland spent £2.857m capital against a £2.381m DfT allocation. Yet the DfT Spend
                scorecard is AMBER — the rating reflects national benchmarking, not just whether spend exceeds
                allocation. For your claim, the more relevant figure is reactive maintenance rising from 15% of spend
                in 2020/21 to 27% in 2024/25.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the A1 through Rutland?
              </h3>
              <p className="text-surface-600 text-sm">
                Rutland County Council is not responsible for the A1 and its slip roads — those are managed by
                National Highways. Rutland maintains 77km of other A-roads, 221km of B/C roads and 222km of
                unclassified roads. Check the National Street Gazetteer for responsibility before claiming.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.rutland.gov.uk/roads-transport-parking/highways/department-transport-funding/highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Rutland County Council Highways Maintenance Transparency Report (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
